import { useEffect, useMemo, useRef, useState } from "react";

import Button from "../../../ui/buttons/Button";
import { whatsappService } from "../../../services/whatsapp.service";
import { extractApiErrorMessage } from "../../../utils/apiError";
import {
  describeTags,
  emptyParameters,
  missingParameters,
  renderTemplatePreview,
  valuesForNames,
} from "../../../utils/whatsappTemplates";

import styles from "./LeadWhatsApp.module.css";

export const NO_TEMPLATES_MESSAGE =
  "Todavía no hay plantillas aprobadas en esta cuenta de WhatsApp Business. Creá una: Meta la revisa, en general en minutos u horas, y después aparece acá.";
export const TEMPLATES_ORIGIN_HINT =
  "Plantillas aprobadas de tu cuenta de WhatsApp Business.";
export const PREFILLED_HINT =
  "Completamos los tags con los datos del lead y del contacto. Revisalos antes de enviar.";
export const MISSING_TAGS_HINT =
  "Faltan datos para algunos tags: completalos a mano.";
export const UNSUPPORTED_TEMPLATE_MESSAGE =
  "Todavía no se puede enviar desde acá: usa un encabezado con imagen, video o documento, botones con datos variables u otras partes que MaqMailing no arma.";
export const TEMPLATES_ERROR_MESSAGE = "No se pudieron cargar las plantillas.";

const categoryLabel = (category) =>
  ({ MARKETING: "Marketing", UTILITY: "Utilidad", AUTHENTICATION: "Autenticación" })[
    (category || "").toUpperCase()
  ] || category || "";

const isNamed = (template) => template?.parameter_format === "NAMED";

const namesOf = (template, part) =>
  template?.[`${part}_parameter_names`] ??
  Array.from({ length: Number(template?.[`${part}_parameters`]) || 0 }, (_, index) => String(index + 1));

const ParameterFields = ({ label, names, values, onChange, prefix, tagLabels, named }) =>
  values.length === 0 ? null : (
    <div className={styles.templateParameters}>
      <span className={styles.formLabel}>{label}</span>
      {values.map((value, index) => {
        const tagLabel = named ? tagLabels[names[index]] || names[index] : null;
        return (
          <label className={styles.templateParameterRow} key={`${prefix}-${index}`}>
            {tagLabel && <span className={styles.templateTagPill}>{tagLabel}</span>}
            <input
              className={styles.formInput}
              value={value}
              onChange={(event) => {
                const next = [...values];
                next[index] = event.target.value;
                onChange(next);
              }}
              placeholder={tagLabel ? `Valor de ${tagLabel}` : `{{${index + 1}}}`}
              aria-label={tagLabel ? `${label}: ${tagLabel}` : `${label} ${index + 1}`}
            />
          </label>
        );
      })}
    </div>
  );

const WhatsAppTemplatePicker = ({
  connectionId,
  threadId = null,
  leadId = null,
  onSend,
  sending = false,
  onClose,
  onManageTemplates,
}) => {
  const dialogRef = useRef(null);
  const [templates, setTemplates] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const [parameters, setParameters] = useState([]);
  const [headerParameters, setHeaderParameters] = useState([]);

  useEffect(() => {
    const trigger = document.activeElement;
    dialogRef.current?.focus();
    return () => {
      if (trigger && typeof trigger.focus === "function" && document.contains(trigger)) trigger.focus();
    };
  }, []);
  const [sendError, setSendError] = useState(null);

  useEffect(() => {
    let mounted = true;
    if (!connectionId) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    Promise.all([
      whatsappService.listTemplates(connectionId),
      threadId
        ? whatsappService.getThreadTags(threadId, { leadId }).catch(() => [])
        : Promise.resolve([]),
    ])
      .then(([{ results }, threadTags]) => {
        if (!mounted) return;
        setTemplates(results);
        setTags(threadTags || []);
      })
      .catch((requestError) => {
        if (mounted) {
          setError(extractApiErrorMessage(requestError) || TEMPLATES_ERROR_MESSAGE);
        }
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [connectionId, threadId, leadId]);

  const tagLabels = useMemo(
    () => Object.fromEntries(tags.map((tag) => [tag.code, tag.label])),
    [tags],
  );
  const tagValues = useMemo(
    () => Object.fromEntries(tags.map((tag) => [tag.code, tag.value])),
    [tags],
  );

  const prefilled = (template, part) =>
    isNamed(template)
      ? valuesForNames(namesOf(template, part), tagValues)
      : emptyParameters(template[`${part}_parameters`]);

  const preview = useMemo(
    () => renderTemplatePreview(selected, parameters, headerParameters),
    [selected, parameters, headerParameters],
  );

  const choose = (template) => {
    if (!template.sendable) return;
    setSelected(template);
    setParameters(prefilled(template, "body"));
    setHeaderParameters(prefilled(template, "header"));
    setSendError(null);
  };

  const incomplete = missingParameters(parameters) || missingParameters(headerParameters);
  const selectedNamed = isNamed(selected);

  const submit = async () => {
    if (!selected || sending || incomplete) return;
    setSendError(null);
    try {
      await onSend?.({
        templateName: selected.name,
        language: selected.language,
        parameters,
        headerParameters,
      });
    } catch (requestError) {
      setSendError(extractApiErrorMessage(requestError) || "No se pudo enviar la plantilla.");
    }
  };

  return (
    <div
      ref={dialogRef}
      className={styles.templatePicker}
      role="dialog"
      aria-label="Plantillas de WhatsApp"
      tabIndex={-1}
      onKeyDown={(event) => {
        if (event.key === "Escape") onClose?.();
      }}
    >
      <div className={styles.templatePickerHeader}>
        <span className={styles.templatePickerTitle}>
          {selected ? `Plantilla: ${selected.name}` : "Elegí una plantilla"}
        </span>
        <div className={styles.templatePickerActions}>
          {!selected && onManageTemplates && templates.length > 0 && (
            <button type="button" className={styles.linkButton} onClick={onManageTemplates}>
              Crear plantilla
            </button>
          )}
          {selected && (
            <button type="button" className={styles.linkButton} onClick={() => setSelected(null)}>
              Cambiar
            </button>
          )}
          <button
            type="button"
            className={styles.linkButton}
            onClick={() => onClose?.()}
            aria-label="Cerrar plantillas"
          >
            Cerrar
          </button>
        </div>
      </div>

      {!selected && <p className={styles.formHint}>{TEMPLATES_ORIGIN_HINT}</p>}

      {loading ? (
        <p className={styles.formHint}>Cargando plantillas...</p>
      ) : error ? (
        <div className={styles.templateEmpty}>
          <p className={styles.composerError} role="alert">
            {error}
          </p>
          {onManageTemplates && (
            <Button variant="primary" tone="green" onClick={onManageTemplates}>
              Crear plantilla
            </Button>
          )}
        </div>
      ) : templates.length === 0 ? (
        <div className={styles.templateEmpty}>
          <p className={styles.formHint} role="status">
            {NO_TEMPLATES_MESSAGE}
          </p>
          {onManageTemplates && (
            <Button variant="primary" tone="green" onClick={onManageTemplates}>
              Crear plantilla
            </Button>
          )}
        </div>
      ) : !selected ? (
        <ul className={styles.templateList}>
          {templates.map((template) => {
            const key = `${template.name}-${template.language}`;
            const open = previewing === key;
            return (
              <li key={key} className={styles.templateItem}>
                <button
                  type="button"
                  className={styles.templateOption}
                  onClick={() => choose(template)}
                  disabled={!template.sendable}
                  title={template.sendable ? "" : UNSUPPORTED_TEMPLATE_MESSAGE}
                >
                  <span className={styles.templateOptionHeader}>
                    <span className={styles.templateName}>{template.name}</span>
                    <span className={styles.templateMeta}>
                      {[template.language, categoryLabel(template.category)]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </span>
                  <span className={styles.templateBody}>
                    {describeTags(template.body_text, tagLabels)}
                  </span>
                  {!template.sendable && (
                    <span className={styles.templateNote}>{UNSUPPORTED_TEMPLATE_MESSAGE}</span>
                  )}
                </button>
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={() => setPreviewing(open ? null : key)}
                  aria-expanded={open}
                >
                  {open ? "Ocultar vista previa" : "Ver vista previa"}
                </button>
                {open && (
                  <div className={styles.templatePreview} aria-label={`Vista previa de ${template.name}`}>
                    {renderTemplatePreview(
                      template,
                      prefilled(template, "body"),
                      prefilled(template, "header"),
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <div className={styles.templateForm}>
          {selectedNamed && (
            <p className={styles.formHint}>
              {incomplete ? MISSING_TAGS_HINT : PREFILLED_HINT}
            </p>
          )}
          <ParameterFields
            label="Encabezado"
            prefix="header"
            names={namesOf(selected, "header")}
            values={headerParameters}
            onChange={setHeaderParameters}
            tagLabels={tagLabels}
            named={selectedNamed}
          />
          <ParameterFields
            label="Cuerpo"
            prefix="body"
            names={namesOf(selected, "body")}
            values={parameters}
            onChange={setParameters}
            tagLabels={tagLabels}
            named={selectedNamed}
          />
          <div className={styles.templatePreview} aria-label="Vista previa">
            {preview}
          </div>
          {sendError && (
            <p className={styles.composerError} role="alert">
              {sendError}
            </p>
          )}
          <div className={styles.formActions}>
            <Button
              variant="primary"
              tone="green"
              loading={sending}
              disabled={incomplete}
              onClick={submit}
            >
              Enviar plantilla
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppTemplatePicker;
